import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Building2, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function ClinicSwitcher() {
  const { clinics, activeClinic, switchClinic } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (!clinics?.length) return null;

  const choose = async (c) => {
    if (c.id === activeClinic?.id) {
      setOpen(false);
      return;
    }
    setOpen(false);
    try {
      await switchClinic(c.id);
      toast.success(`Cambiaste a ${c.name}`);
    } catch {
      toast.error("No se pudo cambiar de clínica");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm"
        data-testid="clinic-switcher"
      >
        <span
          className="w-6 h-6 rounded-md text-white grid place-items-center text-[10px] font-bold"
          style={{ backgroundColor: activeClinic?.color || "#0F172A" }}
        >
          {activeClinic?.name?.[0] || "·"}
        </span>
        <span className="font-medium text-slate-900 hidden sm:inline max-w-[160px] truncate">{activeClinic?.name || "Clínica"}</span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-xl shadow-elevated z-30 overflow-hidden animate-scale-in">
          <div className="px-4 py-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-100 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Cambiar clínica
          </div>
          <ul className="py-1 max-h-80 overflow-y-auto">
            {clinics.map((c) => {
              const active = c.id === activeClinic?.id;
              return (
                <li key={c.id}>
                  <button
                    onClick={() => choose(c)}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 ${active ? "bg-blue-50/50" : ""}`}
                    data-testid={`clinic-option-${c.id}`}
                  >
                    <span className="w-9 h-9 rounded-lg text-white grid place-items-center font-bold" style={{ backgroundColor: c.color || "#0F172A" }}>
                      {c.name[0]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{c.name}</div>
                      {/* TEN.4: sólo los campos con valor. Una clínica dada de alta por
                          el checkout no tiene especialidad ni ciudad, y se pintaba " · " suelto. */}
                      {[c.specialty, c.city].filter((x) => x && x.trim()).length > 0 && (
                        <div className="text-[11px] text-slate-500 truncate">
                          {[c.specialty, c.city].filter((x) => x && x.trim()).join(" · ")}
                        </div>
                      )}
                    </div>
                    {active && <Check className="w-4 h-4 text-emerald-600" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
